import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { HeartPulse, BookOpenText, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const GUIDE = [
  ['Recruitment / TalentCheck', '/client/talentcheck y /client/recruitment',
   'Abrir "Ejecutivo de Ventas Industriales — Monterrey" (cerrado): candidatos con resultados reales, informes publicados y candidata CONTRATADA (Lorena Castañeda). Luego "Coordinador de Logística — CDMX" (activo, decisión pendiente).',
   'Los puntajes provienen del motor real de calificación. Nunca presentar resultados sintéticos como administraciones reales.'],
  ['TalentCompetency / Brechas', '/client/talentcompetency',
   'Mostrar 3 puestos formales con perfiles, evaluaciones de conocimientos (92/100 aprobada, en progreso, asignada desde TalentTrain), mapa de cobertura por departamento y panel de evaluaciones pendientes.',
   'Las brechas muestran "sin evaluación" por diseño: las psicometrías jamás se fabrican.'],
  ['Succession', '/client/succession',
   'Abrir "Gerente de Operaciones": perfil de continuidad (8 secciones), escenario ideal (4 cuadrantes), valoraciones de preparación, plan de desarrollo con enlace a TalentTrain y documento de transferencia.',
   'Cobertura 100% ≠ sucesores listos: la preparación se evalúa por separado.'],
  ['Climara', '/client/climara',
   'Reporte publicado Ola 1 (clima 4.32, interpretación profesional) + Ola 2 activa al 40% + propuesta asesora de Team Building derivada de los resultados.',
   'Nunca se exponen respuestas individuales; solo agregados aprobados por CTH.'],
  ['Comunicaciones', '/client/communications y Mi Portal → Mi Empresa',
   'Comunicación "Taller de integración" con carrusel de 3 imágenes, confirmación de asistencia con cupo, lista de asistentes y recordatorios (solo en la app).',
   'No se envían correos reales desde el demo.'],
  ['Workforce Records', '/client/workforce-records',
   'Matriz de cumplimiento con documentos adjuntos de ejemplo (certificado NOM-029, identificación) en el panel "Documentos / Evidencias" del expediente.',
   'Los documentos son sintéticos y marcados DEMO.'],
  ['TalentPerformance / TalentFix / TalentExit / TalentHeart / TalentTrain', 'menú lateral del cliente',
   'Ciclos 2025 (cerrado con resultados) y 2026 (activo); 4 casos TalentFix en distintas etapas; 2 casos de salida (incl. desactivación programada); celebraciones y reconocimientos; 9+ acciones formativas con evidencia.',
   'No modificar los usuarios reales de capacitación (Mariela Vergara, Nelkis De La Espada).'],
];

export default function AdminDemoHealth() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const r = await api.get('/admin/demo/health'); setData(r.data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-demo-health-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <HeartPulse className="h-6 w-6 text-teal-700" />
            Demo Grupo Novera — Salud y guía interna
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Uso interno CTH (hm_admin / asesores). No visible para clientes.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="demo-health-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {data && (
        <Card className="p-5" data-testid="demo-health-card">
          <div className="flex items-center gap-2 mb-4">
            {data.overall_ok
              ? <Badge className="bg-emerald-600 text-white border-0" data-testid="demo-health-overall-ok">Demo saludable</Badge>
              : <Badge className="bg-amber-500 text-white border-0" data-testid="demo-health-overall-warn">Revisar faltantes</Badge>}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {data.suites.map((s) => (
              <div key={s.suite} className={`rounded-md border px-3 py-2 ${s.ok ? '' : 'border-amber-400 bg-amber-50'}`}
                   data-testid={`demo-health-suite-${s.suite.replace(/[^a-zA-Z]/g, '')}`}>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  {s.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  {s.suite}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {s.checks.map((c) => `${c.collection}: ${c.count}/${c.minimum}`).join(' · ')}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.relationships.map((r) => (
              <Badge key={r.name} variant="outline"
                     className={`text-[10px] ${r.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                {r.name}: {r.ok ? 'OK' : 'FALLA'}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5" data-testid="demo-guide-card">
        <h2 className="font-semibold flex items-center gap-2 mb-4">
          <BookOpenText className="h-5 w-5 text-sky-700" /> Guía de demostración paso a paso
        </h2>
        <ol className="space-y-4">
          {GUIDE.map(([suite, route, demo, note], i) => (
            <li key={suite} className="rounded-lg border p-3">
              <p className="text-sm font-semibold">{i + 1}. {suite}
                <span className="ml-2 font-mono text-xs text-muted-foreground">{route}</span>
              </p>
              <p className="text-sm mt-1">{demo}</p>
              <p className="text-xs text-amber-700 mt-1">⚠ {note}</p>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}
